/*
 * 1DS JS SDK Analytics Web, 3.1.9
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
import { __extendsFn as __extends } from "@microsoft/applicationinsights-shims";
/**
* ApplicationInsights.ts
* @author Abhilash Panwar (abpanwar) and Hector Hernandez (hectorh)
* @copyright Microsoft 2018
* Main class containing all the APIs.
*/
import { AppInsightsCore, LoggingSeverity, _ExtendedInternalMessageId, isString, isFunction, doPerf, objForEachKey, proxyAssign, isNullOrUndefined, isArray, dumpObj, throwError } from "@microsoft/1ds-core-js";
import { PropertiesPlugin } from "@microsoft/1ds-properties-js";
import { PostChannel } from "@microsoft/1ds-post-js";
import { ApplicationInsights as WebAnalytics } from "@microsoft/1ds-wa-js";
// This is an exclude list of properties that should not be updated during initialization
// They include a combination of private and internal property names
var _ignoreUpdateSnippetProperties = ["snippet", "_webAnalytics", "_postChannel", "_propertyManager", "_extensions"];
var ApplicationInsights = /** @class */function (_super) {
  __extends(ApplicationInsights, _super);
  function ApplicationInsights() {
    var _this = _super.call(this) || this;
    // Initialize plugins
    _this._postChannel = new PostChannel();
    _this._propertyManager = new PropertiesPlugin();
    _this._webAnalytics = new WebAnalytics();
    return _this;
  }
  /**
   * Initialize the SKU.
   * @param config        - SKU configuration.
   * @param extensions          - An array of extensions that are to be used by the core.
   */
  ApplicationInsights.prototype.initialize = function (config, extensions) {
    var _this = this;
    var _self = this;
    doPerf(_self, function () {
      return "ApplicationInsights:initialize";
    }, function () {
      var plugins = [_self._propertyManager, _self._webAnalytics];
      if (extensions) {
        plugins = plugins.concat(extensions);
      }
      if (!config) {
        throwError("You must provide a config object!");
      }
      if (config.channels && config.channels.length > 0) {
        // Add post channel to first fork if not available
        var postFound = false;
        for (var j = 0; j < config.channels[0].length; j++) {
          if (config.channels[0][j].identifier === _self._postChannel.identifier) {
            postFound = true;
            break;
          }
        }
        if (!postFound) {
          config.channels[0].push(_self._postChannel);
        }
      } else {
        config.channels = [[_self._postChannel]];
      }
      // Add configurations
      var extConfig = config.extensionConfig = config.extensionConfig || [];
      extConfig[_self._postChannel.identifier] = config ? config.channelConfiguration : {};
      extConfig[_self._propertyManager.identifier] = config ? config.propertyConfiguration : {};
      extConfig[_self._webAnalytics.identifier] = config ? config.webAnalyticsConfiguration : {};
      try {
        _super.prototype.initialize.call(_this, config, plugins);
      } catch (error) {
        _self.logger.throwInternal(LoggingSeverity.CRITICAL, _ExtendedInternalMessageId.FailedToInitializeSDK, "Failed to initialize SDK." + dumpObj(error));
      }
    }, function () {
      return {
        config: config,
        extensions: extensions
      };
    });
  };
  /**
   * Gets the property manager to set custom properties and system properties (part A), that should be applied
   * to all events or events with a specific instrumentation key.
   * @returns {PropertiesPlugin} The property manager object.
   */
  ApplicationInsights.prototype.getPropertyManager = function () {
    return this._propertyManager;
  };
  /**
   * Gets the post channel to configure and set the transmission profiles.
   * @returns {PostChannel} The post channel object.
   */
  ApplicationInsights.prototype.getPostChannel = function () {
    return this._postChannel;
  };
  /**
   * Gets the Web Analytics extension.
   * @returns {WebAnalytics} The Web Analytics extension.
   */
  ApplicationInsights.prototype.getWebAnalyticsExtension = function () {
    return this._webAnalytics;
  };
  /**
   * Telemetry initializers are used to modify the contents of collected telemetry before being sent from the user's browser.
   * They can also be used to prevent certain telemetry from being sent, by returning false.
   * @param telemetryInitializer - Telemetry Initializer
   */
  ApplicationInsights.prototype.addTelemetryInitializer = function (telemetryInitializer) {
    this._webAnalytics.addTelemetryInitializer(telemetryInitializer);
  };
  /**
  * API to send custom event
  * @param event - Custom event
  * @param properties - Custom event properties (part C)
  */
  ApplicationInsights.prototype.trackEvent = function (event, customProperties) {
    this._webAnalytics.trackEvent(event, customProperties);
  };
  /**
  * API to send pageView event
  * @param pageViewEvent - PageView event
  * @param properties - PageView properties (part C)
  */
  ApplicationInsights.prototype.trackPageView = function (pageViewEvent, pageViewProperties) {
    this._webAnalytics.trackPageView(pageViewEvent, pageViewProperties);
  };
  /**
  * API to send pageAction event
  * @param pageActionEvent - PageAction event
  * @param properties - PageAction properties(Part C)
  */
  ApplicationInsights.prototype.trackPageAction = function (pageActionEvent, pageActionProperties) {
    this._webAnalytics.trackPageAction(pageActionEvent, pageActionProperties);
  };
  /**
  * API to send ContentUpdate event
  * @param contentUpdateEvent - ContentUpdate event
  * @param properties - ContentUpdate properties(Part C)
  */
  ApplicationInsights.prototype.trackContentUpdate = function (contentUpdateEvent, properties) {
    this._webAnalytics.trackContentUpdate(contentUpdateEvent, properties);
  };
  /**
  * API to send PageUnload event
  * @param pageUnloadEvent - PageUnload event
  * @param properties - PageUnload properties(Part C)
  */
  ApplicationInsights.prototype.trackPageUnload = function (pageUnloadEvent, properties) {
    this._webAnalytics.trackPageUnload(pageUnloadEvent, properties);
  };
  /**
  * API to send Exception event
  * @param exception - Exception event
  * @param customProperties - Exception properties (part C)
  */
  ApplicationInsights.prototype.trackException = function (exception, customProperties) {
    this._webAnalytics.trackException(exception, customProperties);
  };
  /**
  * API to send PageViewPerformance event
  * @param pageViewPerformance - PageViewPerformance event
  * @param customProperties - PageViewPerformance properties (part C)
  */
  ApplicationInsights.prototype.trackPageViewPerformance = function (pageViewPerformance, customProperties) {
    this._webAnalytics.trackPageViewPerformance(pageViewPerformance, customProperties);
  };
  /**
  * API to create and send a populated PageView event
  * @param overrideValues - Override values
  * @param customProperties - Custom properties(Part C)
  */
  ApplicationInsights.prototype.capturePageView = function (overrideValues, customProperties) {
    this._webAnalytics.capturePageView(overrideValues, customProperties);
  };
  /**
  * API to create and send a populated PageViewPerformance event
  * @param pageViewPerformance - PageViewPerformance event
  * @param customProperties - Custom properties(Part C)
  */
  ApplicationInsights.prototype.capturePageViewPerformance = function (overrideValues, customProperties) {
    this._webAnalytics.capturePageViewPerformance(overrideValues, customProperties);
  };
  /**
  * API to create and send a populated PageAction event
  * @param element - DOM element
  * @param overrideValues - PageAction overrides
  * @param customProperties - Custom properties(Part C)
  * @param isRightClick - Flag for mouse right clicks
  */
  ApplicationInsights.prototype.capturePageAction = function (element, overrideValues, customProperties, isRightClick) {
    this._webAnalytics.capturePageAction(element, overrideValues, customProperties, isRightClick);
  };
  /**
  * API to create and send a populated ContentUpdate event
  * @param overrideValues - ContentUpdate overrides
  * @param customProperties - Custom properties(Part C)
  */
  ApplicationInsights.prototype.captureContentUpdate = function (overrideValues, customProperties) {
    this._webAnalytics.captureContentUpdate(overrideValues, customProperties);
  };
  /**
  * API to create and send a populated PageUnload event
  * @param overrideValues - PageUnload overrides
  * @param customProperties - Custom properties(Part C)
  */
  ApplicationInsights.prototype.capturePageUnload = function (overrideValues, customProperties) {
    this._webAnalytics.capturePageUnload(overrideValues, customProperties);
  };
  /**
  * @description Custom error handler for Application Insights Analytics
  * @param exception
  */
  ApplicationInsights.prototype._onerror = function (exception) {
    this._webAnalytics._onerror(exception);
  };
  /**
  * Call any functions that were queued before the main script was loaded
  * @param snippet
  */
  ApplicationInsights.prototype.emptySnippetQueue = function (snippet) {
    var _self = this;
    function _updateSnippetProperties() {
      if (snippet) {
        var snippetVer = "";
        if (!isNullOrUndefined(_self._snippetVersion)) {
          snippetVer += _self._snippetVersion;
        }
        // TODO (newylie): Need somewhere in Common Schema to put this value
        // let propManager = _self.getPropertyManager();
        // if (propManager) {
        //     let context = propManager.getPropertiesContext();
        //     if (_self.context && _self.context.internal) {
        //         _self.context.internal.snippetVer = snippetVer || "-";
        //     }
        // }
        // apply updated properties to the global instance (snippet)
        objForEachKey(_self, function (field, value) {
          if (isString(field) && !isFunction(value) && field && field[0] !== "_" &&
          // Don't copy "internal" values
          _ignoreUpdateSnippetProperties.indexOf(field) === -1) {
            snippet[field] = value;
          }
        });
      }
    }
    // call functions that were queued before the main script was loaded
    try {
      _updateSnippetProperties();
      if (isArray(snippet.queue)) {
        // note: do not check length in the for-loop conditional in case something goes wrong and the stub methods are not overridden.
        var length = snippet.queue.length;
        for (var i = 0; i < length; i++) {
          var call = snippet.queue[i];
          call();
        }
        snippet.queue = undefined;
        delete snippet.queue;
      }
    } catch (exception) {
      var properties = {};
      if (exception && isFunction(exception.toString)) {
        properties.exception = exception.toString();
      }
    }
  };
  /**
  * Overwrite the lazy loaded fields of global window snippet to contain the
  * actual initialized API methods
  * @param snippet
  */
  ApplicationInsights.prototype.updateSnippetDefinitions = function (snippet) {
    var _self = this;
    // Assign the snippet to this instance
    _self.snippet = snippet;
    _self._snippetVersion = "" + (snippet.sv || snippet.version || "");
    // apply full appInsights to the global instance
    // Note: This will be called BEFORE this instance has been initialized
    proxyAssign(snippet, this, function (name) {
      // Not excluding names prefixed with "_" as we need to proxy some functions like _onError
      return name && _ignoreUpdateSnippetProperties.indexOf(name) === -1;
    });
  };
  return ApplicationInsights;
}(AppInsightsCore);
export { ApplicationInsights };